import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageOpen, Search } from 'lucide-react';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import ReportExportButton from '@/components/reports/ReportExportButton';

export default function StockValuationSection() {
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [includeZero, setIncludeZero] = useState(false);

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'Product'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return res.data.message || [];
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['stockValuation', category, search, includeZero],
    queryFn: async () => {
      const res = await posApi.getStockValuationReport({
        category: category || undefined,
        item: search.trim() || undefined,
        include_zero: includeZero ? 1 : 0,
      });
      return res.data.message as {
        rows: any[];
        summary: {
          sku_count: number;
          total_qty: number;
          total_stock_value: number;
          total_retail_value: number;
        };
      };
    },
  });

  const rows = data?.rows || [];
  const summary = data?.summary;

  const handleExport = () => {
    if (!rows.length) return;
    const headers = [
      'Item Code',
      'Item Name',
      'Variant',
      'Category',
      'UOM',
      'Qty',
      'Cost',
      'Sale Price',
      'Stock Value',
      'Retail Value',
    ];
    const csvRows = rows.map((r) =>
      [
        r.item_code,
        `"${(r.item_name || '').replace(/"/g, '""')}"`,
        `"${(r.variant_name || '').replace(/"/g, '""')}"`,
        `"${(r.category_name || '').replace(/"/g, '""')}"`,
        r.unit_of_measurement || '',
        r.available_stock,
        r.cost_price,
        r.sale_price,
        r.stock_value,
        r.retail_value,
      ].join(','),
    );
    const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_valuation_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">SKUs</div>
          <div className="text-xl font-bold text-gray-900 mt-1">{summary?.sku_count ?? '—'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Total qty</div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {summary ? Number(summary.total_qty).toLocaleString() : '—'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Stock value (cost)</div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {summary ? formatMoney(summary.total_stock_value) : '—'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Retail value</div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {summary ? formatMoney(summary.total_retail_value) : '—'}
          </div>
        </div>
      </div>

      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select className="input-field" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {(categoriesData || []).map((c: any) => (
              <option key={c.name} value={c.name}>
                {c.category_name || c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input-field pl-9"
              placeholder="Item name, code or barcode"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={includeZero}
            onChange={(e) => setIncludeZero(e.target.checked)}
          />
          Include zero stock
        </label>
        <ReportExportButton onClick={handleExport} disabled={!rows.length} />
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          Failed to load stock valuation.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap min-w-[960px]">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Variant</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th>
                <th className="px-4 py-3 font-medium text-right">Cost</th>
                <th className="px-4 py-3 font-medium text-right">Sale price</th>
                <th className="px-4 py-3 font-medium text-right">Stock value</th>
                <th className="px-4 py-3 font-medium text-right">Retail value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading valuation…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <PackageOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    No stock to value for this filter.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.item_code}-${r.variant_name || ''}-${idx}`} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.item_code}</div>
                    </td>
                    <td className="px-4 py-3">{r.variant_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.category_name || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{r.available_stock}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(r.cost_price)}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(r.sale_price)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatMoney(r.stock_value)}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(r.retail_value)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
